import { useState, useCallback, useRef } from 'react';

/**
 * Custom hook for managing event data with undo/redo functionality
 * @param {Object} initialData - Initial event data
 * @returns {Object} Event data state and update functions
 */
export const useEventData = (initialData) => {
  const [eventData, setEventData] = useState(initialData);
  const [history, setHistory] = useState([initialData]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const maxHistorySize = 50; // Limit history size for performance

  /**
   * Update a single field in event data
   */
  const updateField = useCallback((field, value) => {
    setEventData((prev) => {
      const newData = { ...prev, [field]: value };
      
      // Add to history (remove future history if we're not at the end)
      setHistory((prevHistory) => {
        const newHistory = [...prevHistory.slice(0, historyIndex + 1), newData];
        // Limit history size
        if (newHistory.length > maxHistorySize) {
          return newHistory.slice(-maxHistorySize);
        }
        return newHistory;
      });
      
      setHistoryIndex((prev) => {
        const newIndex = prev + 1;
        return newIndex >= maxHistorySize ? maxHistorySize - 1 : newIndex;
      });
      
      return newData;
    });
  }, [historyIndex]);

  /**
   * Update multiple fields at once
   */
  const updateFields = useCallback((updates) => {
    setEventData((prev) => {
      const newData = { ...prev, ...updates };
      
      setHistory((prevHistory) => {
        const newHistory = [...prevHistory.slice(0, historyIndex + 1), newData];
        if (newHistory.length > maxHistorySize) {
          return newHistory.slice(-maxHistorySize);
        }
        return newHistory;
      });
      
      setHistoryIndex((prev) => {
        const newIndex = prev + 1;
        return newIndex >= maxHistorySize ? maxHistorySize - 1 : newIndex;
      });
      
      return newData;
    });
  }, [historyIndex]);

  /**
   * Undo last change
   */
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setEventData(history[newIndex]);
    }
  }, [history, historyIndex]);

  /**
   * Redo last undone change
   */
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setEventData(history[newIndex]);
    }
  }, [history, historyIndex]);

  /**
   * Reset to initial data
   */
  const reset = useCallback(() => {
    setEventData(initialData);
    setHistory([initialData]);
    setHistoryIndex(0);
  }, [initialData]);

  /**
   * Check if data has changed from initial
   */
  const hasChanges = useRef(false);
  if (JSON.stringify(eventData) !== JSON.stringify(initialData)) {
    hasChanges.current = true;
  }

  return {
    eventData,
    updateField,
    updateFields,
    undo,
    redo,
    reset,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
    hasChanges: hasChanges.current,
  };
};

